import ReactPlayer from 'react-player'

import ReactMarkdown, { uriTransformer } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import * as React from 'react'

const IPFS_SERVER = 'https://nfts.forgottenrunes.com/ipfs' //TODO: env var
const CLOUDINARY_SERVER = `https://res.cloudinary.com/dopby768d/image/fetch/` //TODO: env var

const ImageWithFallback = ({ src, fallbackSrc, ...props }: any) => {
  const [imgSrc, setImgSrc] = React.useState<string>(src)
  const onError = () => {
    console.log(`Error loading ${src}, will try ${fallbackSrc}`)
    setImgSrc(fallbackSrc)
  }
  return <img {...props} src={imgSrc} onError={onError} />
}

const TOKEN_TAG_REGEX =
  /(\@(wizard|soul|pony|beast|beastspawn|warrior)([0-9]+))|(Aldo's Isle|Allesar's Keep|Asmodeus's Surf|Asphodel Meadows|Atlanta's Pool|Avalon|Bamboo Forests|Baobabs|BattleMage Mountains|Blue Wizard Bastion|Brown Wizard Delta|Bubonioc Beaches|Buffkin Plains|Bugbear Boonies|Calista's Citadel|Chroma Crystal Row|Chronomancer's Riviera|Cuckoo Land and the Psychic Leap|Donut Atoll|Dread Tower|Dream Master Lake|Duck Alliance Headquarters|Elysian Fields|Elysian Gates|Forest of the Imps|Fort Turtle|Frog Master Marsh|Fur Gnome Wold|Ghoulish Boglands|Goat Grasslands|Goblin Town|Great Owl Obelisk|Green Wizard City|Halcyon Sanctum|Hall of Cats|Hedge Wizard Wood|Honor Mountain|House of Spirits|Hue Master's Pass|Kelpie's Bay|Kobold's Crossroad|Lake of Lanterns|Lenora's Lighthouse|Manticore Valley|Mount Titan|Muscle Mountain|Obsidian City|Omega Oxbow|Purple Wizard Pavilion|Rabbit Lake|Raptor Caye|Red Wizard Capital|Skylord Rookery|The Alchemist's Archipelago|The Bloodfalls|The Carnival Pass|The Cave of the Platonic Shadow|The Court of Chaos Magic|The Dark Sentinel|The Gate to the Seventh Realm|The Infinity Veil|The Nightmare Dominion|The Quantum Shadow|The Sacred Pillars|The Salt|The Sand|The Savannah|The Secret Tower|The Spike|The Thorn|The Toadstools|The Unicorn Gardens|The Weird House|The Wild|Torment Manor|Valley of the Void Desciple|Vampyre Mist|White Wizard Tower|Wolf Dungeon|Yellow Wizard Haven|Zaros Oasis)/gm

const TOKEN_TAG_REGEX_SINGLE =
  /\@(wizard|soul|pony|beast|beastspawn|warrior)([0-9]+)/

export function getCloudinaryFrontedImageSrc(src?: string, flags?: string) {
  let fallbackSrc: string
  let newSrc: string

  const CLOUDINARY_SERVER_WITH_FLAGS = `${CLOUDINARY_SERVER}${
    flags ? flags : 'f_auto'
  }/`

  if (src?.startsWith('ipfs://')) {
    newSrc = src.replace(
      /^ipfs:\/\//,
      `${CLOUDINARY_SERVER_WITH_FLAGS}${IPFS_SERVER}/`
    )
    // We fall back to IPFS CDN if we get error (e.g. in case being over limit with Cloudinary)
    fallbackSrc = src.replace(/^ipfs:\/\//, `${IPFS_SERVER}/`)
  } else if (src?.startsWith('https://') || src?.startsWith('http://')) {
    newSrc = `${CLOUDINARY_SERVER_WITH_FLAGS}${src}`
    fallbackSrc = src
  } else if (src?.startsWith('data')) {
    newSrc = src
    fallbackSrc = src
  } else {
    newSrc = uriTransformer(src as string)
    fallbackSrc = newSrc
  }
  return { newSrc, fallbackSrc }
}

const LoreMarkdownRenderer = ({ markdown }: { markdown: any }) => {
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
        children={markdown}
        components={{
          pre: ({ node, children, ...props }) => (
            <pre {...props} style={{ whiteSpace: 'pre-line' }}>
              {children}
            </pre>
          ),
          a: (props) => {
            if (props.href?.startsWith('https://www.youtube.com')) {
              return <ReactPlayer url={props.href} width={'100%'} />
            }
            if (props.href?.startsWith('@')) {
              const match = props.href.match(TOKEN_TAG_REGEX_SINGLE)

              if (!match || match.length < 3) return <p>{props.title}</p>

              //   const tagType = match[1] // e.g. "wizard"
              //   const tokenId = parseInt(match[2])

              const title = props.children?.[0]

              return (
                <span>
                  {title ? `${title} ` : ''}
                  {props.href}
                </span>
              )
            } else {
              return <a {...props} />
            }
          },
          p: ({ node, children, ...props }) => {
            const processedChildren = []

            for (let i = 0; i < children.length; i++) {
              const child = children[i]

              if (typeof child === 'string') {
                //Character tagging
                const tokenTagMatches = [...child.matchAll(TOKEN_TAG_REGEX)]

                if (tokenTagMatches.length > 0) {
                  tokenTagMatches.forEach((match, index) => {
                    const priorMatchEnd =
                      index === 0
                        ? 0
                        : // @ts-ignore
                          tokenTagMatches[index - 1].index +
                          // @ts-ignore
                          tokenTagMatches[index - 1][0].length

                    processedChildren.push(
                      child.slice(priorMatchEnd, match.index)
                    )

                    if (match[0].startsWith('@')) {
                      // Character tag
                      //   const tagType = match[2] // e.g. "wizard"
                      //   const slug = getSlugFromTag(tagType)
                      //   const tokenId = parseInt(match[3])

                      processedChildren.push(<>{match[0]}</>)
                    } else {
                      // Place
                      const place = match[0] // e.g. "Goblin Town"

                      processedChildren.push(
                        <a
                          key={`${i}-${index}`}
                          href={`https://wizzypedia.forgottenrunes.com/${place.replace(
                            / /g,
                            '_'
                          )}`}
                          target={'_blank'}
                        >
                          {place}
                        </a>
                      )
                    }

                    if (index === tokenTagMatches.length - 1) {
                      const fullMatchedWord = match[0] // e.g. @wizard123
                      processedChildren.push(
                        // @ts-ignore
                        child.slice(match.index + fullMatchedWord.length)
                      )
                    }
                  })
                } else {
                  processedChildren.push(child)
                }
              } else {
                // do nothing, pass through
                processedChildren.push(child)
              }
            }

            return (
              <p
                {...props}
                style={{
                  wordWrap: 'break-word',
                  marginBottom: '8px',
                  marginTop: '8px',
                }}
              >
                {processedChildren}
              </p>
            )
          },
          img: ({ node, src, ...props }) => {
            // @ts-ignore
            const { newSrc, fallbackSrc } = getCloudinaryFrontedImageSrc(src)

            return (
              <ImageWithFallback
                src={newSrc}
                fallbackSrc={fallbackSrc}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            )
          },
        }}
      />
    </>
  )
}

export default LoreMarkdownRenderer
